import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const COLL = { c1: "Exclusive", c2: "Wolfpack", c3: "PUPS" };
const STANDIN = { c1: "/art/c1_exclusive.webp", c2: "/art/frost.webp", c3: "/art/c3_pups.webp" };

function NftCard({ t, active, onPick, busy, inPack, onPack, packFull }) {
  return (
    <div data-testid={`mypack-nft-${t.key}-${t.token_id}`} className={`rounded-2xl overflow-hidden border bg-[#061016] transition-[border-color,transform] duration-300 hover:-translate-y-0.5 ${active ? "border-amber-400 nft-glow" : "border-slate-700/70"}`}>
      <div className="relative aspect-square">
        <img src={t.image} alt={t.name} loading="lazy" className="w-full h-full object-cover bg-slate-900"
          onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = "1"; e.currentTarget.src = STANDIN[t.key]; } }} />
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/70 text-[10px] font-mono-game text-slate-200">{COLL[t.key]}</span>
        {!t.real_art && <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-black/70 text-[10px] text-slate-400">stand-in art</span>}
      </div>
      <div className="p-3">
        <p className="font-display text-base text-white truncate">{t.name}</p>
        {t.attributes.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2 max-h-14 overflow-hidden">
            {t.attributes.slice(0, 5).map((a) => (
              <span key={a.trait_type} title={a.trait_type} className="px-1.5 py-0.5 rounded bg-slate-800/80 text-[10px] text-slate-300">{a.value}</span>
            ))}
          </div>
        )}
        <button data-testid={`mypack-pick-${t.key}-${t.token_id}`} disabled={busy || active} onClick={() => onPick(t)}
          className={`mt-3 w-full py-1.5 rounded-lg text-xs font-semibold ${active ? "bg-amber-400/20 text-amber-200" : "border border-fuchsia-500/50 text-fuchsia-200 hover:bg-fuchsia-500/10"}`}>
          {active ? <span className="flex items-center justify-center gap-1"><Check className="w-3.5 h-3.5" />Your character</span> : "Play as this"}
        </button>
        {!active && (
          <button data-testid={`mypack-pack-${t.key}-${t.token_id}`} disabled={busy || (!inPack && packFull)} onClick={() => onPack(t)}
            className={`mt-1.5 w-full py-1.5 rounded-lg text-xs ${inPack ? "bg-cyan-500/20 text-cyan-100" : "border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 disabled:opacity-40"}`}>
            {inPack ? "In your pack · remove" : packFull ? "Pack full" : "Add to pack"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function MyPack({ game, refresh }) {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const ch = game.wolf?.character;

  const load = useCallback(async () => {
    const { data } = await api.get("/nft/my-pack");
    setItems(data.items);
    setTotal(data.total);
    return data.items.some((x) => x.pending);
  }, []);

  useEffect(() => {
    let tries = 0, timer;
    const run = async () => { if ((await load()) && ++tries < 6) timer = setTimeout(run, 4000); };
    run();
    return () => clearTimeout(timer);
  }, [load, game.wallet?.address]);

  const pick = async (t) => {
    if (!game.wolf) return toast.error("Create your wolf first, then pick a character.");
    setBusy(true);
    try { await api.post("/game/character", { key: t.key, token_id: t.token_id }); await refresh(); toast.success(`You now play as ${t.name}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const pack = game.wolf?.pack || [];
  const inPack = (t) => pack.some((p) => p.key === t.key && p.token_id === t.token_id);
  const togglePack = async (t) => {
    if (!game.wolf) return toast.error("Create your wolf first.");
    const next = inPack(t) ? pack.filter((p) => !(p.key === t.key && p.token_id === t.token_id)) : [...pack, t];
    setBusy(true);
    try { await api.post("/game/pack", { members: next.map((p) => ({ key: p.key, token_id: p.token_id })) }); await refresh(); toast.success(inPack(t) ? `${t.name} left the pack` : `${t.name} joined your pack`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const clear = async () => { await api.post("/game/character", {}); await refresh(); toast("Back to your stock wolf"); };

  if (!items) return <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-fuchsia-400" /></div>;
  return (
    <section data-testid="mypack-gallery" className="mt-14">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl text-white">My Pack</h2>
          <p className="text-sm text-slate-400">{items.length ? `${total} held${total > items.length ? ` (showing ${items.length})` : ""}. Pick one to play as. Your level, skills and items stay the same.` : "No wolves or PUPS from the three collections in this wallet yet."}</p>
        </div>
        {ch && <button data-testid="mypack-clear-btn" onClick={clear} className="text-sm text-slate-400 hover:text-white">Use stock wolf instead</button>}
      </div>
      {items.length > 0 && (
        <div data-testid="mypack-team" className="mb-8 rounded-2xl border border-cyan-500/30 bg-cyan-950/20 p-4">
          <p className="text-sm text-cyan-100 font-semibold mb-1">Your battle pack</p>
          <p className="text-xs text-slate-400 mb-4">You lead, and up to 2 of your NFT wolves fight beside you. Each companion strikes every turn for about 16% of your attack.</p>
          <div className="flex gap-4">
            {[{ lead: true, name: ch?.name || game.wolf?.name, image: game.avatar }, pack[0], pack[1]].map((s, i) => (
              <div key={i} data-testid={`mypack-slot-${i}`} className="text-center w-20">
                {s ? <img src={s.image} alt={s.name} className={`w-20 h-20 rounded-xl object-cover border ${i === 0 ? "border-amber-400" : "border-cyan-400/70"}`} />
                  : <div className="w-20 h-20 rounded-xl border border-dashed border-slate-600 grid place-items-center text-[10px] text-slate-500">Empty</div>}
                <p className="text-[10px] text-slate-300 mt-1 truncate">{s ? (i === 0 ? "Leader" : s.name) : "Companion"}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {items.map((t) => (
          <NftCard key={`${t.key}-${t.token_id}`} t={t} busy={busy} onPick={pick} active={ch?.key === t.key && ch?.token_id === t.token_id}
            inPack={inPack(t)} onPack={togglePack} packFull={pack.length >= 2} />
        ))}
      </div>
    </section>
  );
}
